import { merge } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useProfileContext } from "Auth/contexts";
import { clearDataByKey, dataMetaSelector, getBandIcal, icalSelector } from "Bands/state";
import {
  BAND_ACTIONS,
  IBand,
  IBandIcal,
  IBandIcalParams,
  IEventStatus,
  IIconBandSetting,
  PeopleBlockStatus,
} from "Bands/types";
import { eventStatuses, peopleBlockStatuses, useClearActionMetaOnUnMount } from "Bands/utils";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIState, IDataKeys } from "shared/types";

interface Props {
  bandId: string;
  band: IBand | null;
  eventTypes?: IIconBandSetting[];
}

export const useModalLogic = ({
  bandId,
  band,
  eventTypes,
}: Props): {
  getApiStatus: IAPIState | undefined;
  editApiStatus: IAPIState | undefined;
  iCal?: IBandIcal | null;
  defaultValues: IBandIcalParams;
} => {
  const { currentUser } = useProfileContext();
  const { api: getApiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.getBandIcal));
  const { api: editApiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.editBandIcal));

  useClearActionMetaOnUnMount(BAND_ACTIONS.getBandIcal);
  useClearActionMetaOnUnMount(BAND_ACTIONS.editBandIcal);

  const dispatch = useDispatch();

  const iCal = useSelector(icalSelector);

  const defaultValues = useMemo((): IBandIcalParams => {
    if (iCal) {
      return merge({}, iCal, {
        eventTypes: iCal?.eventTypes?.map(({ id }) => id),
      });
    }

    return {
      eventTypes: eventTypes?.map(({ id }) => API_ENDPOINTS.EVENT_TYPES(id)),
      eventStatuses: eventStatuses
        .filter(({ value }) => value !== IEventStatus.postponed && value !== IEventStatus.canceled)
        .map(({ value }) => value),
      eventRsvpStatuses: peopleBlockStatuses
        .filter(({ value }) => value !== PeopleBlockStatus.not_attending && value !== "none")
        .map(({ value }) => value),
      showBirthdays: false,
      splitSchedule: false,
    };
  }, [eventTypes, iCal]);

  const [fetchedOnce, setFetchedOnce] = useState(false);

  useEffect(() => {
    if (!bandId) return;
    if (eventTypes?.length === 0) return;
    if (fetchedOnce) return;

    const bandPath = API_ENDPOINTS.BAND(bandId || "");
    const user = API_ENDPOINTS.USER(currentUser?.username || "");

    setFetchedOnce(true);

    const cleanDefaultValues = {
      ...defaultValues,
      eventRSVPStatuses: defaultValues.eventRsvpStatuses,
    };

    dispatch(
      getBandIcal({
        title: `TM: ${band?.title}`,
        band: bandPath,
        user,
        data: cleanDefaultValues,
      })
    );
  }, [dispatch, bandId, band, currentUser, defaultValues, eventTypes, fetchedOnce]);

  // clean up all ical links on unmount
  useEffect(() => {
    return () => {
      dispatch(clearDataByKey(IDataKeys.ical));
    };
  }, [dispatch]);

  return {
    getApiStatus: getApiMeta?.status,
    editApiStatus: editApiMeta?.status,
    iCal,
    defaultValues,
  };
};
